import CopyableId from "@/components/common/CopyableId";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Map, { Marker, Popup, Source, Layer, NavigationControl } from "react-map-gl";
import FleetLocator from "@/pages/fleet/FleetLocator";
import { BreakdownBar, SpecChip } from "@/pages/fleet/RecBreakdown";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  Stethoscope, Heart, AlertTriangle, RefreshCw, Truck, MapPin, Filter,
  Users, ClipboardList, ChevronRight, Clock, CheckCircle2,
  Route as RouteIcon, ChevronLeft, CalendarDays, Navigation, X,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { DEFAULT_CENTER, VISIT_PRIORITY_COLORS, READINESS_COLORS, SOURCE_LABELS, timeAgo, spreadOverlaps } from "@/pages/fleet/constants";
import { useMapboxToken } from "@/hooks/useMapboxToken";

const REFRESH_MS = 30000;
const LIVE_REFRESH_MS = 3000; // live tracking polls every 3s so a moving provider updates near real-time

const todayRiyadh = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Riyadh", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
const shiftDay = (iso, delta) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
};
const fmtDayLabel = (iso) => {
  try {
    const d = new Date(`${iso}T12:00:00Z`);
    const label = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
    return iso === todayRiyadh() ? `Today · ${label}` : label;
  } catch { return iso; }
};
const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Riyadh" }); }
  catch { return ""; }
};

function ClinicianPin({ clinician }) {
  const c = READINESS_COLORS[clinician.readiness] || READINESS_COLORS.unavailable;
  return (
    <div className="relative" data-testid={`fleet-clinician-pin-${clinician.clinician_id}`}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white"
        style={{ background: c.dot }}>
        <Stethoscope className="w-4 h-4 text-white" />
      </div>
      <div className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full ring-2 ring-white" style={{ background: c.dot }} />
    </div>
  );
}

function PatientPin({ patient }) {
  const atHomeOnly = !patient.has_active_visit;
  const c = VISIT_PRIORITY_COLORS[patient.highest_priority] || VISIT_PRIORITY_COLORS.routine;
  // Patients at home without an active visit get a distinct teal "home" pin.
  const bg = atHomeOnly ? "#0B7E8C" : c.border;
  return (
    <div className="relative" data-testid={`fleet-patient-pin-${patient.mrn}`}>
      <div className={`w-7 h-7 flex items-center justify-center shadow-lg ring-2 ring-white ${atHomeOnly ? "rounded-full" : "rounded-md"}`}
        style={{ background: bg }}>
        {atHomeOnly ? <MapPin className="w-3.5 h-3.5 text-white" /> : <Heart className="w-3.5 h-3.5 text-white" />}
      </div>
      {patient.visit_count > 1 && (
        <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#0B7E8C] text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white">
          {patient.visit_count}
        </div>
      )}
    </div>
  );
}

function VehiclePin({ vehicle }) {
  return (
    <div className="relative" data-testid={`fleet-vehicle-pin-${vehicle.plate_number}`}>
      <div className="w-7 h-7 rounded-md flex items-center justify-center shadow-lg ring-2 ring-white bg-[#475569]">
        <Truck className="w-3.5 h-3.5 text-white" />
      </div>
    </div>
  );
}

// Spread markers that share identical coordinates into a small ring so
// stacked pins (e.g. patients geocoded to the same city centroid) are visible.
// Spread markers that share identical coordinates into a small ring so
// stacked pins are visible — shared helper lives in constants.js.

export default function FleetMap() {
  const MAPBOX_TOKEN = useMapboxToken();
  const navigate = useNavigate();
  const location = useLocation();
  const mapRef = useRef(null);
  const focusHandled = useRef(false);
  const [data, setData] = useState({ clinicians: [], patients: [], vehicles: [] });
  const [loading, setLoading] = useState(true);
  const [popup, setPopup] = useState(null);
  const [showClin, setShowClin] = useState(true);
  const [showPat, setShowPat] = useState(true);
  const [showVeh, setShowVeh] = useState(true);
  const [showLive, setShowLive] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Recommendations side-panel state
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [recReq, setRecReq] = useState(null);
  const [recBusy, setRecBusy] = useState(false);
  const [narrative, setNarrative] = useState(null);

  // Clinician route overlay state
  const [routeClin, setRouteClin] = useState(null);   // { clinician_id, name }
  const [routeDay, setRouteDay] = useState(todayRiyadh);
  const [routeData, setRouteData] = useState(null);
  const [routeBusy, setRouteBusy] = useState(false);

  const loadRoute = useCallback(async (clin, day) => {
    if (!clin) return;
    setRouteBusy(true);
    try {
      const r = await api.get("/fleet/my-route", { params: { clinician_id: clin.clinician_id, day } });
      setRouteData(r.data);
      const pts = (r.data.stops || []).map((s) => [s.longitude, s.latitude]);
      if (r.data.start) pts.push([r.data.start.longitude, r.data.start.latitude]);
      if (pts.length === 1) {
        mapRef.current?.flyTo({ center: pts[0], zoom: 13, duration: 1000, essential: true });
      } else if (pts.length > 1) {
        const lngs = pts.map((p) => p[0]); const lats = pts.map((p) => p[1]);
        mapRef.current?.fitBounds(
          [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
          { padding: 80, duration: 1000, maxZoom: 14 });
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRouteBusy(false); }
  }, []);

  useEffect(() => { if (routeClin) loadRoute(routeClin, routeDay); }, [routeClin, routeDay, loadRoute]);

  const clearRoute = useCallback(() => { setRouteClin(null); setRouteData(null); }, []);
  const showClinicianRoute = useCallback((clin) => {
    setSelectedVisit(null); setRouteDay(todayRiyadh()); setRouteClin(clin); setPopup(null);
  }, []);
  const routeStops = routeData?.stops || [];

  const load = useCallback(async () => {
    try {
      const r = await api.get("/fleet/map");
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  // Live visit tracking overlay — breadcrumb trails + moving clinician markers
  const [liveSessions, setLiveSessions] = useState([]);
  const loadLiveSessions = useCallback(async () => {
    try {
      const r = await api.get("/visit-tracking/active");
      setLiveSessions(r.data.items || []);
    } catch {
      // silent — non-critical, will retry on next poll
    }
  }, []);
  useEffect(() => { loadLiveSessions(); }, [loadLiveSessions]);
  useEffect(() => {
    if (!autoRefresh) return;
    // Poll live tracking much faster than the full fleet snapshot so a moving
    // provider's marker updates without waiting for the next 30s refresh.
    const t = setInterval(loadLiveSessions, LIVE_REFRESH_MS);
    return () => clearInterval(t);
  }, [autoRefresh, loadLiveSessions]);
  // Toast the dispatcher once for each newly-flagged away session
  const alertedRef = useRef(new Set());
  useEffect(() => {
    for (const s of liveSessions) {
      if (s.away_flagged && !alertedRef.current.has(s.id)) {
        alertedRef.current.add(s.id);
        toast.warning(`Provider ${s.clinician_name || "clinician"} is away from site — Visit still active`, {
          duration: 8000,
        });
      }
      if (!s.away_flagged) alertedRef.current.delete(s.id);
    }
  }, [liveSessions]);

  // Any clinician with an active tracking session is rendered as a LIVE marker
  // below; suppress their roster pin here to avoid a duplicate on the map.
  const liveClinicianIds = useMemo(
    () => new Set(liveSessions.map((s) => s.clinician_id).filter(Boolean)),
    [liveSessions],
  );
  const displayClinicians = useMemo(
    () => spreadOverlaps(data.clinicians.filter((c) => !liveClinicianIds.has(c.clinician_id))),
    [data.clinicians, liveClinicianIds],
  );
  const displayPatients = useMemo(() => spreadOverlaps(data.patients), [data.patients]);
  const displayVehicles = useMemo(() => spreadOverlaps(data.vehicles), [data.vehicles]);

  const flyToEntity = useCallback((kind, item) => {
    const list = kind === "patient" ? displayPatients : kind === "clinician" ? displayClinicians : displayVehicles;
    const idKey = kind === "patient" ? "patient_id" : kind === "clinician" ? "clinician_id" : "id";
    const disp = list.find((x) => x[idKey] === item[idKey]) || item;
    const lng = disp._lng ?? disp.longitude;
    const lat = disp._lat ?? disp.latitude;
    if (!lat || !lng) {
      toast.error("No location pin available for this entry.");
      return;
    }
    // Zoom in tighter for stacked entries so spread pins separate visibly.
    mapRef.current?.flyTo({ center: [lng, lat], zoom: disp._stacked ? 17 : 15, duration: 1200, essential: true });
    setPopup({ kind, item: disp });
  }, [displayPatients, displayClinicians, displayVehicles]);

  // Fly to a target passed via navigation state (from the Fleet Dashboard).
  useEffect(() => {
    if (loading || focusHandled.current) return;
    const focus = location.state?.focus;
    if (!focus) return;
    let item;
    if (focus.kind === "patient") item = data.patients.find((p) => p.patient_id === focus.id);
    else if (focus.kind === "clinician") item = data.clinicians.find((c) => c.clinician_id === focus.id);
    else if (focus.kind === "vehicle") item = data.vehicles.find((v) => v.id === focus.id);
    if (item) {
      focusHandled.current = true;
      setTimeout(() => flyToEntity(focus.kind, item), 400);
    }
  }, [loading, data, location.state, flyToEntity]);

  async function fetchRecommendations(visit) {
    setSelectedVisit(visit); setRecBusy(true); setRecommendations([]); setNarrative(null); setRecReq(null);
    try {
      const r = await api.post(`/fleet/visits/${visit.id}/recommend`);
      setRecommendations(r.data.recommendations || []);
      setRecReq(r.data.requirements || null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRecBusy(false); }
  }
  async function fetchNarrative() {
    if (!selectedVisit) return;
    setRecBusy(true);
    try {
      const r = await api.post(`/fleet/visits/${selectedVisit.id}/recommend/explain`);
      setNarrative(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRecBusy(false); }
  }
  async function assignClinician(clinicianId, ai_accepted) {
    if (!selectedVisit) return;
    try {
      await api.post(`/fleet/visits/${selectedVisit.id}/assign`, { clinician_id: clinicianId, ai_accepted });
      toast.success("Visit assigned");
      setSelectedVisit(null); setRecommendations([]); setNarrative(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  if (!MAPBOX_TOKEN) {
    return (
      <div className="hah-card p-8 text-center text-sm text-[#6B7280]">
        Loading map…
      </div>
    );
  }

  return (
    <div data-testid="fleet-map-page">
      <PageHeader
        title="Live Operations Map"
        subtitle="Real-time positions for clinicians, all onboarded patients at home, and fleet vehicles."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setAutoRefresh((a) => !a)} className="border-[#E5E7EB]" data-testid="fleet-map-autorefresh">
              {autoRefresh ? "Auto ON" : "Auto OFF"}
            </Button>
            <Button variant="outline" onClick={() => { load(); loadLiveSessions(); }} className="border-[#E5E7EB]" data-testid="fleet-map-refresh">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
          </div>
        }
      />

      <div className="hah-card p-3 mb-3 flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]"><Filter className="w-3.5 h-3.5 inline mr-1" />Layers:</span>
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={showClin} onChange={(e) => setShowClin(e.target.checked)} data-testid="fleet-map-toggle-clinicians" />
          <Stethoscope className="w-3.5 h-3.5 text-[#0B7E8C]" /> Clinicians ({data.clinicians.length})
        </label>
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={showPat} onChange={(e) => setShowPat(e.target.checked)} data-testid="fleet-map-toggle-patients" />
          <MapPin className="w-3.5 h-3.5 text-[#EF4444]" /> Patients ({data.patients.length})
        </label>
        <span className="text-[10px] text-[#94A3B8] inline-flex items-center gap-2 ml-1">
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#0B7E8C]" /> at home</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#EF4444]" /> active visit</span>
        </span>
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={showVeh} onChange={(e) => setShowVeh(e.target.checked)} data-testid="fleet-map-toggle-vehicles" />
          <Truck className="w-3.5 h-3.5 text-[#475569]" /> Vehicles ({data.vehicles.length})
        </label>
        <label className={`flex items-center gap-1.5 text-xs cursor-pointer ${liveSessions.length ? "font-semibold text-[#10B981]" : ""}`}
               data-testid="fleet-map-toggle-live">
          <input type="checkbox" checked={showLive} onChange={(e) => setShowLive(e.target.checked)} />
          <span className={`relative inline-flex w-2.5 h-2.5 rounded-full ${liveSessions.some((s) => s.away_flagged) ? "bg-[#EF4444]" : "bg-[#10B981]"}`}>
            {liveSessions.length > 0 && (
              <span className={`absolute inline-flex w-full h-full rounded-full opacity-70 animate-ping ${liveSessions.some((s) => s.away_flagged) ? "bg-[#EF4444]" : "bg-[#10B981]"}`}></span>
            )}
          </span>
          Live tracking ({liveSessions.length})
        </label>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* MAP */}
        <div className="lg:col-span-2 hah-card overflow-hidden" style={{ height: "640px" }}>
          <Map
            ref={mapRef}
            mapboxAccessToken={MAPBOX_TOKEN}
            initialViewState={DEFAULT_CENTER}
            mapStyle="mapbox://styles/mapbox/light-v11"
            style={{ width: "100%", height: "100%" }}
          >
            <NavigationControl position="top-right" />
            {showClin && displayClinicians.map((c) => (
              <Marker key={c.clinician_id} longitude={c._lng} latitude={c._lat} anchor="center"
                style={{ cursor: "pointer" }}
                onClick={(e) => { e.originalEvent.stopPropagation(); setPopup({ kind: "clinician", item: c }); }}>
                <ClinicianPin clinician={c} />
              </Marker>
            ))}
            {showPat && displayPatients.map((p) => (
              <Marker key={p.patient_id} longitude={p._lng} latitude={p._lat} anchor="center"
                style={{ cursor: "pointer" }}
                onClick={(e) => { e.originalEvent.stopPropagation(); setPopup({ kind: "patient", item: p }); }}>
                <PatientPin patient={p} />
              </Marker>
            ))}
            {showVeh && displayVehicles.map((v) => (
              <Marker key={v.id} longitude={v._lng} latitude={v._lat} anchor="center"
                style={{ cursor: "pointer" }}
                onClick={(e) => { e.originalEvent.stopPropagation(); setPopup({ kind: "vehicle", item: v }); }}>
                <VehiclePin vehicle={v} />
              </Marker>
            ))}
            {/* Clinician route overlay */}
            {routeData?.geometry && (
              <Source id="clin-route" type="geojson" data={{ type: "Feature", geometry: routeData.geometry }}>
                <Layer id="clin-route-line" type="line"
                       layout={{ "line-cap": "round", "line-join": "round" }}
                       paint={{ "line-color": "#0B7E8C", "line-width": 4, "line-opacity": 0.85 }} />
              </Source>
            )}
            {routeData?.start && (
              <Marker longitude={routeData.start.longitude} latitude={routeData.start.latitude} anchor="center">
                <span className="w-6 h-6 rounded-full bg-[#1F2937] ring-2 ring-white shadow flex items-center justify-center"
                      data-testid="fleet-route-start"
                      title={`${routeData.start.label || "Route start"} · ${routeData.start.latitude.toFixed(4)}, ${routeData.start.longitude.toFixed(4)}`}>
                  <Navigation className="w-3 h-3 text-white" />
                </span>
              </Marker>
            )}
            {routeStops.map((s) => (
              <Marker key={`rs-${s.visit_id}`} longitude={s.longitude} latitude={s.latitude} anchor="center">
                <span className="w-7 h-7 rounded-full bg-[#0B7E8C] text-white text-[12px] font-bold ring-2 ring-white shadow flex items-center justify-center"
                      data-testid={`fleet-route-stop-${s.stop_number}`}>
                  {s.stop_number}
                </span>
              </Marker>
            ))}

            {/* --- Live visit tracking overlay --- */}
            {showLive && liveSessions.map((s) => {
              const crumbs = (s.breadcrumbs || []).map((b) => [b.lng, b.lat]);
              if (crumbs.length < 2) return null;
              return (
                <Source key={`trk-src-${s.id}`} id={`trk-src-${s.id}`} type="geojson"
                        data={{ type: "Feature", geometry: { type: "LineString", coordinates: crumbs } }}>
                  <Layer id={`trk-line-${s.id}`} type="line"
                         layout={{ "line-cap": "round", "line-join": "round" }}
                         paint={{
                           "line-color": s.away_flagged ? "#EF4444" : "#0B7E8C",
                           "line-width": 3,
                           "line-opacity": 0.85,
                           "line-dasharray": [2, 1],
                         }} />
                </Source>
              );
            })}
            {showLive && liveSessions.map((s) => (
              <Marker key={`trk-site-${s.id}`}
                      longitude={s.site_longitude} latitude={s.site_latitude} anchor="center">
                <span className="w-4 h-4 rounded-full bg-white ring-2 ring-[#0B7E8C] shadow flex items-center justify-center"
                      title="Visit site (anchored geofence)">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0B7E8C]" />
                </span>
              </Marker>
            ))}
            {showLive && liveSessions.filter((s) => s.last_latitude && s.last_longitude).map((s) => {
              // Match the live session back to the clinician record so the same
              // popup (name, role, workload, "Show route on map") opens on click.
              const clin = data.clinicians.find((c) => c.clinician_id === s.clinician_id);
              return (
                <Marker key={`trk-live-${s.id}`}
                        longitude={s.last_longitude} latitude={s.last_latitude} anchor="bottom"
                        onClick={(e) => {
                          e.originalEvent.stopPropagation();
                          setPopup({ kind: "clinician", item: clin || {
                            clinician_id: s.clinician_id,
                            name: s.clinician_name,
                            latitude: s.last_latitude,
                            longitude: s.last_longitude,
                          } });
                        }}>
                  <div className="relative flex flex-col items-center cursor-pointer"
                       data-testid={`fleet-live-tracker-${s.clinician_id}`}>
                    {/* Provider name label above the pin */}
                    <div className={`px-2 py-0.5 text-[10px] font-bold rounded-full shadow ring-2 ring-white whitespace-nowrap mb-1
                                     ${s.away_flagged ? "bg-[#EF4444] text-white" : "bg-[#10B981] text-white"}`}>
                      LIVE · {s.clinician_name || "Provider"}
                    </div>
                    <div className="relative">
                      <span className={`absolute inset-0 rounded-full opacity-70 animate-ping ${s.away_flagged ? "bg-[#EF4444]" : "bg-[#10B981]"}`} />
                      <div className={`relative w-10 h-10 rounded-full flex items-center justify-center shadow-xl ring-4 ring-white
                                       ${s.away_flagged ? "bg-[#EF4444]" : "bg-[#10B981]"}`}
                           title={`${s.clinician_name || "Provider"} — ${s.away_flagged ? "AWAY from site" : "on visit"} · ${Math.round(s.last_distance_m || 0)}m from site`}>
                        <Stethoscope className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>
                </Marker>
              );
            })}
            {/* --- end live overlay --- */}

            {popup && (
              <Popup
                longitude={popup.item._lng ?? popup.item.longitude}
                latitude={popup.item._lat ?? popup.item.latitude}
                anchor="top"
                onClose={() => setPopup(null)}
                closeButton
                maxWidth="260px"
              >
                {popup.kind === "clinician" && (
                  <div className="p-1 min-w-[200px]" data-testid="fleet-popup-clinician">
                    <div className="text-sm font-semibold text-[#1F2937]">{popup.item.name}</div>
                    <div className="text-xs text-[#6B7280] capitalize">{(popup.item.role_code || "").replace("_", " ")} · {popup.item.facility_name}</div>
                    <div className="text-xs mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
                      style={{ background: (READINESS_COLORS[popup.item.readiness] || {}).bg, color: (READINESS_COLORS[popup.item.readiness] || {}).text }}>
                      {(READINESS_COLORS[popup.item.readiness] || {}).label}
                    </div>
                    <div className="text-xs text-[#6B7280] mt-1">Workload: {popup.item.workload}/{popup.item.capacity || 6}</div>
                    <div className="text-[10px] text-[#94A3B8] mt-1">Updated {timeAgo(popup.item.updated_at)}</div>
                    <button onClick={() => showClinicianRoute(popup.item)}
                      className="text-[#0B7E8C] hover:underline inline-flex items-center gap-0.5 text-[11px] mt-2 font-medium"
                      data-testid={`fleet-popup-show-route-${popup.item.clinician_id}`}>
                      <RouteIcon className="w-3 h-3" /> Show route on map <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                )}
                {popup.kind === "patient" && (
                  <div className="p-1 min-w-[240px]" data-testid="fleet-popup-patient">
                    <div className="text-sm font-semibold text-[#1F2937]">{popup.item.name}</div>
                    <div className="text-xs text-[#6B7280]"><CopyableId value={popup.item.mrn} kind="MRN" /></div>
                    {popup.item.city && <div className="text-[11px] text-[#94A3B8] mt-0.5"><MapPin className="w-3 h-3 inline mr-0.5" />{popup.item.city}</div>}
                    {!popup.item.has_active_visit ? (
                      <div className="mt-2 text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#0B7E8C14] text-[#0B7E8C] font-medium">
                        At home · no active visit
                      </div>
                    ) : (
                      <div className="text-xs mt-2 font-semibold text-[#475569]">{popup.item.visit_count} scheduled visit(s):</div>
                    )}
                    <div className="space-y-2 mt-1">
                      {popup.item.visits.map((v) => {
                        const pc = VISIT_PRIORITY_COLORS[v.priority] || VISIT_PRIORITY_COLORS.routine;
                        return (
                          <div key={v.id} className="rounded-md border border-[#E5E7EB] p-1.5">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-medium text-[#1F2937] capitalize">{v.type.replace(/_/g, " ")}</span>
                              <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded" style={{ background: pc.border, color: "#fff" }}>{v.priority}</span>
                            </div>
                            <div className="text-[11px] text-[#475569] mt-0.5">
                              <Clock className="w-3 h-3 inline mr-0.5 text-[#94A3B8]" />
                              {v.scheduled_at ? new Date(v.scheduled_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "Unscheduled"}
                            </div>
                            <div className="text-[11px] text-[#475569] mt-0.5 capitalize">
                              <span className="text-[#94A3B8]">Status:</span> {(v.status || "").replace(/_/g, " ")}
                            </div>
                            <div className="text-[11px] text-[#475569] mt-0.5">
                              <Stethoscope className="w-3 h-3 inline mr-0.5 text-[#94A3B8]" />
                              {(v.clinician_names && v.clinician_names.length) ? v.clinician_names.join(", ") : "Unassigned"}
                            </div>
                            <button onClick={() => { setPopup(null); fetchRecommendations(v); }}
                              className="text-[#0B7E8C] hover:underline inline-flex items-center gap-0.5 text-[11px] mt-1"
                              data-testid={`fleet-popup-recommend-${v.id}`}>
                              Recommend clinician <ChevronRight className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <button onClick={() => navigate(`/clinical/patients/${popup.item.patient_id}`)}
                      className="text-[#0B7E8C] hover:underline inline-flex items-center gap-0.5 text-[11px] mt-2"
                      data-testid="fleet-popup-open-patient">
                      Open patient profile <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                )}
                {popup.kind === "vehicle" && (
                  <div className="p-1 min-w-[160px]" data-testid="fleet-popup-vehicle">
                    <div className="text-sm font-semibold text-[#1F2937]">{popup.item.plate_number}</div>
                    <div className="text-xs text-[#6B7280] capitalize">{popup.item.vehicle_type} · {popup.item.status}</div>
                  </div>
                )}
              </Popup>
            )}
          </Map>
        </div>

        {/* SIDE PANEL */}
        <div className="hah-card p-4 max-h-[640px] overflow-auto" data-testid="fleet-side-panel">
          {/* Live tracking sessions widget — always shown when any session is active */}
          {liveSessions.length > 0 && (
            <div className="mb-4 rounded-lg border border-[#10B98133] bg-gradient-to-br from-[#ECFDF5] to-white p-3"
                 data-testid="fleet-live-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-[#10B981]">
                    <span className="absolute inline-flex w-full h-full rounded-full opacity-70 animate-ping bg-[#10B981]"></span>
                  </span>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#065F46]">
                    Live tracking · {liveSessions.length}
                  </span>
                </div>
              </div>
              <div className="space-y-1.5">
                {liveSessions.map((s) => {
                  const distance = Math.round(s.last_distance_m || 0);
                  const distText = distance >= 1000 ? `${(distance / 1000).toFixed(1)} km` : `${distance} m`;
                  const clin = data.clinicians.find((c) => c.clinician_id === s.clinician_id);
                  return (
                    <button
                      key={`live-panel-${s.id}`}
                      onClick={() => {
                        const lng = s.last_longitude || s.site_longitude;
                        const lat = s.last_latitude || s.site_latitude;
                        mapRef.current?.flyTo({
                          center: [lng, lat],
                          zoom: 16, duration: 1200, essential: true,
                        });
                        // Also surface the same provider info card the roster
                        // marker shows, so the dispatcher gets details in one click.
                        setPopup({ kind: "clinician", item: clin || {
                          clinician_id: s.clinician_id,
                          name: s.clinician_name,
                          latitude: lat, longitude: lng,
                        } });
                      }}
                      className={`w-full text-left rounded-md border p-2 hover:bg-white transition
                                  ${s.away_flagged ? "border-[#EF444433] bg-[#FEF2F2]" : "border-[#10B98133] bg-white"}`}
                      data-testid={`fleet-live-session-${s.clinician_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[13px] font-semibold text-[#1F2937] truncate">
                          {s.clinician_name || "Provider"}
                        </div>
                        {s.away_flagged && (
                          <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#EF4444] text-white">
                            Away
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-[#6B7280] truncate">
                        {s.label || s.visit_id?.slice(0, 8)} · {distText} from site
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {routeClin ? (
            <div data-testid="fleet-route-panel">
              <div className="flex items-center justify-between">
                <div>
                  <div className="hah-section-title">Clinician Route</div>
                  <h3 className="text-sm font-semibold text-[#1F2937] mt-0.5 flex items-center gap-1.5">
                    <RouteIcon className="w-4 h-4 text-[#0B7E8C]" /> {routeClin.name}
                  </h3>
                </div>
                <button onClick={clearRoute} className="text-[#6B7280] hover:text-[#0B7E8C]" data-testid="fleet-route-close" aria-label="Clear route">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Day navigator */}
              <div className="flex items-center gap-2 mt-3">
                <div className="inline-flex items-center rounded-lg border border-[#E5E7EB] bg-white overflow-hidden" data-testid="fleet-route-day-nav">
                  <button onClick={() => setRouteDay((d) => shiftDay(d, -1))} className="px-2 py-1.5 hover:bg-[#F8FAFC] text-[#374151]" data-testid="fleet-route-prev-day" aria-label="Previous day">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <label className="relative flex items-center gap-1.5 px-2.5 py-1.5 border-x border-[#E5E7EB] cursor-pointer">
                    <CalendarDays className="w-3.5 h-3.5 text-[#0B7E8C]" />
                    <span className="text-[12px] font-medium text-[#1F2937] whitespace-nowrap" data-testid="fleet-route-day-label">{fmtDayLabel(routeDay)}</span>
                    <input type="date" value={routeDay} onChange={(e) => e.target.value && setRouteDay(e.target.value)}
                           className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" data-testid="fleet-route-day-input" />
                  </label>
                  <button onClick={() => setRouteDay((d) => shiftDay(d, 1))} className="px-2 py-1.5 hover:bg-[#F8FAFC] text-[#374151]" data-testid="fleet-route-next-day" aria-label="Next day">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                {routeDay !== todayRiyadh() && (
                  <button onClick={() => setRouteDay(todayRiyadh())} className="px-2.5 py-1.5 rounded-lg border border-[#E5E7EB] text-[12px] text-[#374151] hover:bg-[#F8FAFC]" data-testid="fleet-route-today">Today</button>
                )}
              </div>

              {routeBusy ? (
                <div className="text-xs text-[#6B7280] py-6 text-center">Loading route…</div>
              ) : routeStops.length === 0 ? (
                <div className="text-xs text-[#94A3B8] italic py-6 text-center" data-testid="fleet-route-empty">
                  No located visits for {fmtDayLabel(routeDay)}.
                  {routeData?.unlocated_count > 0 && (
                    <div className="text-[#F59E0B] mt-1">{routeData.unlocated_count} visit(s) have no map location.</div>
                  )}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    <div className="rounded-lg bg-[#F8FAFC] border border-[#E5E7EB] p-2 text-center">
                      <div className="text-base font-bold text-[#1F2937]">{routeStops.length}</div>
                      <div className="text-[9px] uppercase tracking-wide text-[#94A3B8]">Stops</div>
                    </div>
                    <div className="rounded-lg bg-[#F8FAFC] border border-[#E5E7EB] p-2 text-center">
                      <div className="text-base font-bold text-[#1F2937]">{routeData.distance_km}<span className="text-[10px]"> km</span></div>
                      <div className="text-[9px] uppercase tracking-wide text-[#94A3B8]">Distance</div>
                    </div>
                    <div className="rounded-lg bg-[#F8FAFC] border border-[#E5E7EB] p-2 text-center">
                      <div className="text-base font-bold text-[#1F2937]">{Math.round(routeData.duration_min)}<span className="text-[10px]"> min</span></div>
                      <div className="text-[9px] uppercase tracking-wide text-[#94A3B8]">Drive</div>
                    </div>
                  </div>
                  <div className="space-y-2 mt-3">
                    {routeStops.map((s) => (
                      <div key={s.visit_id} className="flex items-center gap-2.5 p-2 rounded-lg border border-[#F1F5F9]" data-testid={`fleet-route-list-${s.stop_number}`}>
                        <span className="w-7 h-7 rounded-full bg-[#0B7E8C] text-white text-xs font-bold flex items-center justify-center shrink-0">{s.stop_number}</span>
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-medium text-[#1F2937] truncate">{s.patient_name}</div>
                          <div className="text-[11px] text-[#6B7280] capitalize">
                            {s.scheduled_at ? `${fmtTime(s.scheduled_at)} · ` : ""}{(s.visit_type || "").replace(/_/g, " ")}{s.city ? ` · ${s.city}` : ""}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {routeData.source === "fallback" && (
                    <p className="text-[10px] text-[#94A3B8] mt-3">Straight-line estimate (live driving routes temporarily unavailable).</p>
                  )}
                </>
              )}
            </div>
          ) : !selectedVisit ? (
            <div>
              <div className="hah-section-title">Quick stats</div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="rounded-lg bg-[#F0FDF4] border border-[#10B981]/30 p-2">
                  <div className="text-[10px] uppercase font-semibold text-[#065F46]">Clinicians</div>
                  <div className="text-xl font-bold text-[#065F46]">{data.clinicians.length}</div>
                </div>
                <div className="rounded-lg bg-[#FEF2F2] border border-[#EF4444]/30 p-2">
                  <div className="text-[10px] uppercase font-semibold text-[#991B1B]">Patient visits</div>
                  <div className="text-xl font-bold text-[#991B1B]">{data.patients.reduce((a, b) => a + b.visit_count, 0)}</div>
                </div>
                <div className="rounded-lg bg-[#F8FAFC] border border-[#94A3B8]/30 p-2">
                  <div className="text-[10px] uppercase font-semibold text-[#475569]">Vehicles</div>
                  <div className="text-xl font-bold text-[#475569]">{data.vehicles.length}</div>
                </div>
                <div className="rounded-lg bg-[#FFF7ED] border border-[#F97316]/30 p-2">
                  <div className="text-[10px] uppercase font-semibold text-[#9A3412]">Urgent visits</div>
                  <div className="text-xl font-bold text-[#9A3412]">{data.patients.filter((p) => p.highest_priority !== "routine").length}</div>
                </div>
              </div>
              <div className="mt-4 text-xs text-[#6B7280]">Click any patient pin → <strong>Recommend</strong> to see AI clinician suggestions.</div>
              <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
                <FleetLocator data={data} onSelect={flyToEntity} />
              </div>
              <div className="mt-4">
                <Button variant="outline" onClick={() => navigate("/fleet/visits")} className="w-full border-[#E5E7EB]">
                  <ClipboardList className="w-4 h-4 mr-1.5" /> Open Visit Scheduling Board
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="hah-section-title">AI Recommendations</div>
                  <h3 className="text-sm font-semibold text-[#1F2937] mt-0.5 capitalize">{selectedVisit.type.replace(/_/g, " ")}</h3>
                  <div className="text-xs text-[#6B7280]">Priority: {selectedVisit.priority}</div>
                </div>
                <button onClick={() => { setSelectedVisit(null); setRecommendations([]); setNarrative(null); }}
                  className="text-xs text-[#6B7280] hover:text-[#0B7E8C]">Close</button>
              </div>
              {recReq && (recReq.medical_specialty || recReq.sub_specialty || recReq.specialty_type) && (
                <div className="mt-2 mb-1 p-2 rounded-md bg-[#F8FAFC] border border-[#E5E7EB]" data-testid="fleet-rec-requirements">
                  <div className="text-[9px] uppercase tracking-wider font-semibold text-[#6B7280] mb-1">Required specialty</div>
                  <div className="flex flex-wrap gap-1">
                    {recReq.medical_specialty && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#0B7E8C14] text-[#0B7E8C] font-medium">{recReq.medical_specialty}</span>}
                    {recReq.sub_specialty && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#0B7E8C14] text-[#0B7E8C] font-medium">{recReq.sub_specialty}</span>}
                    {recReq.specialty_type && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#64748B1A] text-[#475569] font-medium">{recReq.specialty_type}</span>}
                  </div>
                </div>
              )}
              {recBusy && <div className="text-xs text-[#6B7280] py-3">Computing recommendations…</div>}
              {!recBusy && recommendations.length === 0 && (
                <div className="text-xs text-[#94A3B8] italic py-3">No recommendations available.</div>
              )}
              <div className="space-y-2 mt-2">
                {recommendations.map((r, idx) => (
                  <div key={r.clinician_id} className={`rounded-lg border p-2.5 ${idx === 0 ? "border-[#0B7E8C] bg-[#EAF8FA]" : "border-[#E5E7EB] bg-white"}`}
                    data-testid={`fleet-rec-${idx}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div>
                        <div className="text-sm font-semibold text-[#1F2937]">{r.clinician_name || r.clinician_id}</div>
                        <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">{(r.role_code || "").replace("_", " ")}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-[#0B7E8C]">{r.score}</div>
                        <div className="text-[10px] text-[#6B7280]">score</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-[11px] text-[#475569] mb-2">
                      <span><MapPin className="w-3 h-3 inline mr-0.5" />{r.distance_km}km · {r.eta_minutes}min</span>
                      <span>Load {r.workload_pct}%</span>
                      <span style={{ color: (READINESS_COLORS[r.readiness] || {}).text }}>{(READINESS_COLORS[r.readiness] || {}).label}</span>
                      {r.within_radius === false && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FEF2F2] text-[#991B1B] font-semibold">Out of range</span>
                      )}
                    </div>
                    {(r.medical_specialization || r.sub_specialty || r.specialty_type) && (
                      <div className="flex flex-wrap gap-1 mb-2" data-testid={`fleet-rec-specialty-${idx}`}>
                        {r.medical_specialization && (
                          <SpecChip ok={r.specialty_match?.medical} label={r.medical_specialization} />
                        )}
                        {r.sub_specialty && (
                          <SpecChip ok={r.specialty_match?.sub} label={r.sub_specialty} />
                        )}
                        {r.specialty_type && (
                          <SpecChip ok={r.specialty_match?.level} label={r.specialty_type} />
                        )}
                      </div>
                    )}
                    <BreakdownBar breakdown={r.breakdown} />
                    <div className="text-[10px] text-[#6B7280] mb-2 line-clamp-2">{r.reasons?.join(" · ")}</div>
                    <Button size="sm" onClick={() => assignClinician(r.clinician_id, idx === 0)}
                      className="w-full bg-[#0B7E8C] hover:bg-[#128C97] text-white h-7 text-xs"
                      data-testid={`fleet-rec-assign-${idx}`}>
                      Assign {idx === 0 ? "(accept AI)" : ""}
                    </Button>
                  </div>
                ))}
              </div>
              {recommendations.length > 0 && (
                <div className="mt-3">
                  <Button size="sm" variant="outline" onClick={fetchNarrative} disabled={recBusy}
                    className="w-full border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#F5F3FF]" data-testid="fleet-rec-explain">
                    Why this pick? (LLM)
                  </Button>
                </div>
              )}
              {narrative && (
                <div className="mt-2 p-2.5 rounded bg-[#F5F3FF] border border-[#8B5CF6]/30 text-xs text-[#1F2937]" data-testid="fleet-rec-narrative">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#8B5CF6] mb-1">
                    {narrative.model === "fallback" ? "Deterministic" : narrative.model}
                  </div>
                  {narrative.narrative}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
